import * as Sentry from '@sentry/react';

const environment =
  !import.meta.env.VITE_APP_API_URL || import.meta.env.VITE_APP_API_URL.includes('-test.')
    ? 'development'
    : 'production';

const allowedUrls = [
  window.location.origin,
  'https://t.me',
  'https://telegram.org',
  'https://core.telegram.org',
  'https://api.telegram.org',
  'https://adsgram.me',
  'https://api.adsgram.me',
  'https://mc.yandex.ru',
  'https://metrika.yandex.ru',
];

// Паттерны TON Connect ошибок, которые нужно игнорировать
const TON_CONNECT_IGNORED_ERROR_PATTERNS = [
  // Пользователь отменил подключение - это нормальное поведение
  'Operation aborted',
  'e2 Operation aborted',
  '[TON_CONNECT_SDK_ERROR] e2 Operation aborted',

  // Попытка подключить уже подключенный кошелек - это нормальное поведение
  'Wallet connection called but wallet already connected',
  'Ea: Wallet connection called but wallet already connected',
  '[TON_CONNECT_SDK_ERROR] Ea: Wallet connection called but wallet already connected',

  // Пересоздание ресурса прервано новым созданием - это нормальное поведение SDK
  'Resource recreation was aborted by a new resource creation',
  'e2 Resource recreation was aborted by a new resource creation',
  '[TON_CONNECT_SDK_ERROR] e2 Resource recreation was aborted by a new resource creation',

  // Пользователь отклонил запрос в кошельке - это нормальное поведение
  'The Wallet declined the request',
  '[TON_CONNECT_SDK_ERROR] The Wallet declined the request',

  // Пользователь закрыл модальное окно - это нормальное поведение
  'Modal was closed',
  '[TON_CONNECT_SDK_ERROR] Modal was closed',

  // Таймаут подключения - может происходить при медленном интернете
  'Connection timeout',
  '[TON_CONNECT_SDK_ERROR] Connection timeout',
] as const;

// Упрощенные паттерны для проверки исключений
const TON_CONNECT_EXCEPTION_PATTERNS = [
  'Operation aborted',
  'Wallet connection called but wallet already connected',
  'Resource recreation was aborted',
  'The Wallet declined the request',
  'Modal was closed',
  'Connection timeout',
] as const;

// Регулярное выражение для TON Connect кодов ошибок
const TON_CONNECT_ERROR_CODE_PATTERN = /\b(e2|Ea|E[a-f0-9]+)\b/i;

/**
 * Проверяет, содержит ли текст один из игнорируемых паттернов TON Connect
 */
const containsIgnoredTonConnectPattern = (text: string): boolean => {
  return TON_CONNECT_IGNORED_ERROR_PATTERNS.some((pattern) => text.includes(pattern));
};

/**
 * Проверяет, является ли ошибка TON Connect ошибкой с кодом
 */
const isTonConnectErrorWithCode = (errorName: string, errorMessage: string): boolean => {
  return (
    errorName.includes('TON_CONNECT_SDK_ERROR') &&
    (TON_CONNECT_ERROR_CODE_PATTERN.test(errorMessage) ||
      TON_CONNECT_ERROR_CODE_PATTERN.test(errorName))
  );
};

/**
 * Фильтрует TON Connect ошибки из originalException
 */
const filterTonConnectFromOriginalException = (
  error: unknown,
  event: Sentry.ErrorEvent
): boolean => {
  if (!error || typeof error !== 'object') return false;

  const errorMessage = error.toString() || '';
  const errorName = (error as Error).name || '';

  // Проверяем сообщение об ошибке
  if (
    containsIgnoredTonConnectPattern(errorMessage) ||
    (event.message && containsIgnoredTonConnectPattern(event.message))
  ) {
    console.log('[Sentry Filter] Ignoring TON Connect error:', errorMessage);
    return true;
  }

  // Проверяем название ошибки с кодами
  if (isTonConnectErrorWithCode(errorName, errorMessage)) {
    console.log(
      '[Sentry Filter] Ignoring TON Connect error by code:',
      errorName,
      errorMessage
    );
    return true;
  }

  return false;
};

/**
 * Фильтрует TON Connect ошибки из exception values
 */
const filterTonConnectFromExceptions = (event: Sentry.ErrorEvent): boolean => {
  if (!event.exception?.values) return false;

  for (const exception of event.exception.values) {
    if (exception.value && exception.value.includes('TON_CONNECT_SDK_ERROR')) {
      const isIgnorableError = TON_CONNECT_EXCEPTION_PATTERNS.some((pattern) =>
        exception.value!.includes(pattern)
      );

      if (isIgnorableError) {
        console.log('[Sentry Filter] Ignoring TON Connect exception:', exception.value);
        return true;
      }
    }
  }

  return false;
};

/**
 * Основной фильтр для исключения бесполезных TON Connect ошибок
 */
const beforeSend = (
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null => {
  // Фильтруем TON Connect ошибки из originalException
  if (filterTonConnectFromOriginalException(hint.originalException, event)) {
    return null;
  }

  // Фильтруем TON Connect ошибки из exception values
  if (filterTonConnectFromExceptions(event)) {
    return null;
  }

  return event;
};

const sentryConfig = {
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment,
  enabled: !import.meta.env.DEV,
  beforeSend,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: allowedUrls,
      networkRequestHeaders: ['Authorization', 'Content-Type'],
      networkResponseHeaders: ['Content-Type', 'Cache-Control'],
    }),
    Sentry.browserTracingIntegration(),
    Sentry.replayCanvasIntegration(),
    Sentry.httpContextIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init(sentryConfig);
}
